import { Box, Grid } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Typography } from "@platform/design-system";

import { RfpContext } from "../../contexts";
import { RFP_ACTION_TYPES } from "../../constants";
import { DocumentTypePanel } from "../../components";

import DocumentUpload from "./documentUploadRfp";
import CoverageAssignmentList from "./coverageAssigmentList";
import AdditionalDocumentPanel from "./additionalDocumentsPanel";

const { UPDATE_STILL_PENDING_DOC } = RFP_ACTION_TYPES;

const STEPS = {
  UPLOAD_DOCUMENTS: 0,
  ASSIGN_COVERAGES: 1,
};

const SectionSelectType = ({
  documentTypes = [],
  uploadedFiles = [],
  additionalFiles = [],
}) => {
  const { setRfp } = useContext(RfpContext);
  const [currentPanel, setCurrentPanel] = useState(null);

  const onSelect = (index) => {
    setCurrentPanel((prev) => (prev === index ? null : index));
  };

  return (
    <Grid size={8} pr={3}>
      <Typography variant="header-large" mb={4}>
        Select Document Type
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {documentTypes.map((documentType, index) => (
          <CoverageDocumentTypePanel
            uploadedFiles={uploadedFiles}
            key={`${documentType.documentTypeName}-${currentPanel === index}`}
            documentType={documentType}
            onSelect={() => onSelect(index)}
            isOpen={currentPanel === index}
            setRfp={setRfp}
          />
        ))}
        <AdditionalDocumentPanel
          onSelect={() => onSelect("additionalDocuments")}
          isOpen={currentPanel === "additionalDocuments"}
          documents={additionalFiles}
          uploadedFiles={uploadedFiles}
        />
      </Box>
    </Grid>
  );
};

export default SectionSelectType;

const CoverageDocumentTypePanel = ({
  uploadedFiles = [],
  setRfp,
  documentType,
  onSelect,
  isOpen,
}) => {
  const isEmployeeNavigatorManaged = Boolean(
    documentType?.employeeNavigatorManaged,
  );
  const initialExternals = uploadedFiles
    .filter((f) => f.documentTypes[documentType.documentTypeName])
    .map((f) => f.file);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeStep, setActiveStep] = useState(STEPS.UPLOAD_DOCUMENTS);
  const [files, setFiles] = useState([]);
  const [externalFiles, setExternalFiles] = useState(initialExternals);
  // Use a ref so we can read whether external files exist inside the effect
  // without making it a reactive dependency (which would cause an infinite loop
  // since setExternalFiles inside the effect would flip the value).
  const hasExternalFilesRef = useRef(initialExternals.length > 0);
  hasExternalFilesRef.current = externalFiles.length > 0;

  const documentTypeName = documentType.documentTypeName;
  const requiredList = documentType.coverages.required;
  const optionalList = documentType.coverages.optional;
  const allFiles = [...externalFiles, ...files];
  const hasPendingAssigments = allFiles.some(
    (file) =>
      (
        uploadedFiles.find((f) => f.fileName === file.name)?.documentTypes?.[
          documentTypeName
        ] || []
      ).length === 0,
  );
  const currHasPending =
    isOpen &&
    [...externalFiles, ...files].some(
      (file) =>
        !uploadedFiles.find((f) => f.fileName === file.name)?.documentTypes?.[
          documentTypeName
        ],
    );

  useEffect(() => {
    if (hasExternalFilesRef.current) {
      const currentInitFiles = uploadedFiles
        .filter((f) => f.documentTypes[documentTypeName])
        .map((f) => f.file);
      setExternalFiles(currentInitFiles);
    }
  }, [uploadedFiles, documentTypeName]);

  useEffect(() => {
    if (isOpen) {
      setRfp({ type: UPDATE_STILL_PENDING_DOC, value: currHasPending });
    }
  }, [currHasPending, setRfp, isOpen]);

  const onBack = () => {
    setExternalFiles(initialExternals);
    setFiles(
      files.filter((f) => !initialExternals.find((ie) => ie.name === f.name)),
    );
    setActiveStep(STEPS.UPLOAD_DOCUMENTS);
  };

  const onClose = () => {
    if (currHasPending) return;
    onSelect();
  };

  const removeFile = (fileName) => {
    const newFiles = files.filter((f) => f.name !== fileName);
    const newExternalFiles = externalFiles.filter((f) => f.name !== fileName);
    setFiles(newFiles);
    setExternalFiles(newExternalFiles);
    if (newFiles.length === 0 && newExternalFiles.length === 0) {
      setActiveStep(STEPS.UPLOAD_DOCUMENTS);
    }
  };

  return (
    <DocumentTypePanel
      isOpen={isOpen}
      title={documentType.documentTypeName}
      requiredList={requiredList}
      optionalList={optionalList}
      isRequired={hasPendingAssigments}
      employeeNavigatorManaged={isEmployeeNavigatorManaged}
      onClick={onSelect}
    >
      {isEmployeeNavigatorManaged ? (
        <Typography variant="body-large">
          Your census will be pulled from EmployeeNavigator when this RFP is
          submitted. To upload a file instead, change your selection in the
          previous step.
        </Typography>
      ) : (
        <>
          <DocumentUpload
            initialExternals={initialExternals}
            uploadedFiles={uploadedFiles}
            files={files}
            documentTypeName={documentType.documentTypeName}
            setFiles={setFiles}
            externalFiles={externalFiles}
            setExternalFiles={setExternalFiles}
            onNext={() => setActiveStep(STEPS.ASSIGN_COVERAGES)}
          />
          <CoverageAssignmentList
            files={allFiles}
            closeDisabled={currHasPending}
            documentType={documentType}
            uploadedFiles={uploadedFiles}
            onClose={onClose}
            onBack={onBack}
            removeFile={removeFile}
          />
        </>
      )}
    </DocumentTypePanel>
  );
};
